import React from "react";
import {
  ShieldCheck,
  Check,
  ExternalLink,
  Phone,
  BookOpen,
} from "lucide-react";

const safetyPoints = [
  {
    badge: "Zero Contact",
    badgeColor: "emerald",
    text: "Sealed cartridge system auto-dispenses resin. The printer lid stays closed during printing — no open resin, no fumes.",
  },
  {
    badge: "Gloves Only",
    badgeColor: "amber",
    text: "The only time you wear gloves is transferring parts from the build platform to Form Wash. That's it.",
  },
  {
    badge: "Zero Contact",
    badgeColor: "emerald",
    text: "Form Wash and Form Cure handle all solvent cleaning and UV curing automatically in enclosed chambers.",
  },
  {
    badge: "Safe to Handle",
    badgeColor: "emerald",
    text: "After washing and curing, parts are fully safe to handle bare-handed. Standard room ventilation is all that's needed.",
  },
];

export const EducationSection = () => {
  return (
    <section className="py-24 lg:py-32 bg-[#0A0A0A] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#F76B1C]/[0.02] rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Hero: Image + Safety overview */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 mb-16 lg:mb-20">
          {/* Left: Image */}
          <div className="relative rounded-2xl overflow-hidden min-h-[350px] lg:min-h-[480px]">
            <img loading="lazy"
              src="/images/edu_industry_page.jpg.jpg"
              alt="3D printing in education lab"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/90 text-white text-xs font-bold">
                <ShieldCheck size={13} />
                Safe for Schools, TAFEs & Universities
              </span>
            </div>
          </div>

          {/* Right: Content */}
          <div className="flex flex-col justify-center">
            <span className="text-[#F76B1C] text-sm font-semibold uppercase tracking-[0.15em] mb-3">
              Education & Safety
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-4">
              You Barely Touch the Resin
            </h2>
            <p className="text-white/50 leading-relaxed mb-8">
              The Formlabs workflow is designed so resin contact is virtually
              eliminated. Sealed cartridges, enclosed printing, and automated
              post-processing mean your staff and students can focus on
              learning — not worrying about safety.
            </p>

            {/* Safety points */}
            <div className="space-y-4">
              {safetyPoints.map((point, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span
                    className={`shrink-0 mt-0.5 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${
                      point.badgeColor === "emerald"
                        ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25"
                        : "bg-amber-500/15 text-amber-400 border border-amber-500/25"
                    }`}
                  >
                    <ShieldCheck size={10} />
                    {point.badge}
                  </span>
                  <p className="text-sm text-white/45 leading-relaxed">
                    {point.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Trust strip: education images */}
        <div className="grid grid-cols-3 gap-4 mb-16 lg:mb-20 rounded-2xl overflow-hidden">
          <div className="relative h-48 lg:h-56">
            <img loading="lazy"
              src="/images/ivy_tech_3dp_in_edu_.jpg.jpg"
              alt="Instructor teaching with 3D printed aids"
              className="absolute inset-0 w-full h-full object-cover rounded-2xl"
            />
          </div>
          <div className="relative h-48 lg:h-56">
            <img loading="lazy"
              src="/images/boston_university_2_guide_to_3dp_in_edu.jpg.jpg"
              alt="University 3D printing lab"
              className="absolute inset-0 w-full h-full object-cover rounded-2xl"
            />
          </div>
          <div className="relative h-48 lg:h-56">
            <img loading="lazy"
              src="/images/boston_university_guide_to_3dp_in_edu.jpg.jpg"
              alt="Students using Formlabs printers"
              className="absolute inset-0 w-full h-full object-cover rounded-2xl"
            />
          </div>
        </div>

        {/* Resources + CTA */}
        <div className="rounded-2xl bg-white/[0.04] border border-white/[0.06] p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Left: Resources */}
            <div>
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                Resources for Educators
              </h3>
              <p className="text-white/50 leading-relaxed mb-6">
                We've helped schools, TAFEs, and universities across Australia
                adopt Formlabs SLA printing. Here's everything you need to
                make a confident decision.
              </p>

              <div className="space-y-3 mb-8">
                {[
                  {
                    label: "Safety Data Sheets (SDS) for All Resins",
                    url: "https://formlabs.com/materials/data-sheets/",
                  },
                  {
                    label: "Guide to 3D Printing in Education (White Paper)",
                    url: "https://3d.formlabs.com/white-paper-guide-to-3d-printing-in-education/",
                  },
                  {
                    label: "Formlabs Educator Resources & Curriculum",
                    url: "https://formlabs.com/industries/education/resources/",
                  },
                  {
                    label: "3D Printing Bootcamp — Beginners ($90)",
                    url: "https://www.3dprintergear.com.au/3d-printing-bootcamp-beginners",
                  },
                ].map((res) => (
                  <a
                    key={res.label}
                    href={res.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 group"
                  >
                    <div className="w-6 h-6 rounded-full bg-[#F76B1C]/15 flex items-center justify-center shrink-0">
                      <BookOpen size={12} className="text-[#F76B1C]" />
                    </div>
                    <span className="text-sm text-white/60 group-hover:text-[#F76B1C] transition-colors">
                      {res.label}
                    </span>
                    <ExternalLink
                      size={11}
                      className="text-white/20 group-hover:text-[#F76B1C] transition-colors shrink-0"
                    />
                  </a>
                ))}
              </div>
            </div>

            {/* Right: CTA */}
            <div className="bg-[#F76B1C]/[0.06] rounded-2xl border border-[#F76B1C]/15 p-7 lg:p-8">
              <h4 className="text-xl font-bold text-white mb-2">
                Ready to Bring SLA to Your Institution?
              </h4>
              <p className="text-white/40 text-sm mb-6 leading-relaxed">
                Whether you're a primary school, high school, TAFE, or
                university — we'll help you spec the right setup, handle
                your purchase order, and get your lab running.
              </p>

              <div className="space-y-3">
                <a
                  href="mailto:admin@3dprintergear.com.au?subject=Education%20Enquiry%20-%20Formlabs%20SLA"
                  className="w-full flex items-center justify-center gap-2 py-3 bg-[#F76B1C] hover:bg-[#E05A0F] text-white font-semibold rounded-xl transition-colors text-sm"
                >
                  Request an Education Quote
                </a>
                <a
                  href="tel:1300334327"
                  className="w-full flex items-center justify-center gap-2 py-3 border border-white/[0.1] hover:border-white/[0.2] text-white/70 hover:text-white font-semibold rounded-xl transition-all text-sm hover:bg-white/[0.03]"
                >
                  <Phone size={15} />
                  Call: 1300 334 327
                </a>
              </div>

              <div className="mt-5 pt-4 border-t border-white/[0.08] flex items-start gap-3">
                <Check
                  size={16}
                  className="text-emerald-400 mt-0.5 shrink-0"
                />
                <p className="text-xs text-white/35 leading-relaxed">
                  Government and education accounts receive 30-day payment
                  terms with an official purchase order. Approved supplier
                  since 2012.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
